import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyActionCode,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  reload as reloadFirebaseUser,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  onIdTokenChanged,
  signOut,
  verifyBeforeUpdateEmail
} from "firebase/auth";
import { Routes, Route, Navigate, NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { auth, googleProvider } from "./firebase";
import Dashboard from "./pages/Dashboard";
import Chapters from "./pages/Chapters";
import ChapterHome from "./pages/ChapterHome";
import FrameAttempts from "./pages/FrameAttempts";
import Practice from "./pages/Practice";
import AttemptReview from "./pages/AttemptReview";
import MyQuestions from "./pages/MyQuestions";
import CreateQuestionPage from "./pages/CreateQuestionPage";
import CustomQuestionAttempts from "./pages/CustomQuestionAttempts";
import Admin from "./pages/Admin";
import AdminChapters from "./pages/AdminChapters";
import AdminChapterEditor from "./pages/AdminChapterEditor";
import AdminFrames from "./pages/AdminFrames";
import AdminFrameEditor from "./pages/AdminFrameEditor";
import AdminSolutionGraphs from "./pages/AdminSolutionGraphs";
import AdminSolutionGraphEditor from "./pages/AdminSolutionGraphEditor";
import Profile from "./pages/Profile";
import Users from "./pages/Users";
import TutorDashboard from "./pages/TutorDashboard";
import TutorStudents from "./pages/TutorStudents";
import TutorAssignments from "./pages/TutorAssignments";
import TutorAssignmentDetail from "./pages/TutorAssignmentDetail";
import TutorCalendarPage from "./pages/TutorCalendarPage";
import TutorPerformanceReports from "./pages/TutorPerformanceReports";
import MyTutor from "./pages/MyTutor";
import "./App.css";
import inksolverLogo from "./assets/inksolver-logo.png";
import SolveFirstProblem from "./pages/SolveFirstProblem";
import StartYourJourney from "./pages/StartYourJourney";
import SlateInputLab from "./pages/SlateInputLab";
import CollaborationPractice from "./pages/CollaborationPractice";
import NotificationBell from "./components/NotificationBell";
import AuthScreen from "./components/AuthScreen";
import EmailVerificationScreen from "./components/EmailVerificationScreen";
import PrivacyPolicyOverlay from "./components/PrivacyPolicyOverlay";
import EmailPreferencesPage from "./components/EmailPreferencesPage";
import DeviceCheck from "./pages/DeviceCheck";
import OnboardingProgressHeader from "./components/OnboardingProgressHeader";

const SESSION_WARNING_MS = 15000;
const SESSION_WARNING_SECONDS = Math.floor(SESSION_WARNING_MS / 1000);
const MIN_ROUTE_OVERLAY_MS = 320;
const MAX_ROUTE_OVERLAY_MS = 8000;
const PRIVACY_POLICY_VERSION = "2026-08-27";

function getLocationSignature(location) {
  return `${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}`;
}

function getRouteLoadingMessage(pathname) {
  const normalized = String(pathname || "").trim().toLowerCase();
  if (normalized.startsWith("/tutor")) return "Loading tutor portal...";
  if (normalized.startsWith("/collaborate")) return "Loading collaboration room...";
  if (normalized.startsWith("/practice")) return "Loading practice...";
  if (normalized.startsWith("/chapters")) return "Loading chapters...";
  if (normalized.startsWith("/my-questions")) return "Loading my questions...";
  if (normalized.startsWith("/attempts")) return "Loading attempt review...";
  if (normalized.startsWith("/dashboard")) return "Loading dashboard...";
  if (normalized.startsWith("/admin")) return "Loading admin...";
  return "Loading...";
}

function getDefaultApiBase() {
  if (typeof window === "undefined") return "http://localhost:5000";
  const hostname = String(window.location.hostname || "").trim() || "localhost";
  const protocol = window.location.protocol === "https:" ? "https:" : "http:";
  return `${protocol}//${hostname}:5000`;
}

function friendlyAuthError(error) {
  const code = String(error?.code || "");
  if (String(error?.message || "").startsWith("Admin access denied")) {
    return "This account does not have access to the requested admin page.";
  }
  const messages = {
    "auth/email-already-in-use": "An account already exists for this email. Try logging in instead.",
    "auth/expired-action-code": "This verification link has expired. Request a new email and try again.",
    "auth/invalid-action-code": "This verification link is invalid or has already been used.",
    "auth/invalid-credential": "That email and password combination is not correct.",
    "auth/invalid-email": "Enter a valid email address.",
    "auth/network-request-failed": "We could not reach the sign-in service. Check your connection and try again.",
    "auth/popup-blocked": "Your browser blocked the Google sign-in window. Allow popups and try again.",
    "auth/popup-closed-by-user": "Google sign-in was closed before it finished.",
    "auth/requires-recent-login": "For security, sign in again before changing the account email.",
    "auth/too-many-requests": "Too many attempts were made. Please wait a moment and try again.",
    "auth/unauthorized-domain": "This domain is not yet authorized for sign-in.",
    "auth/user-disabled": "This account has been disabled. Contact InkSolver support.",
    "auth/weak-password": "Choose a stronger password with at least 6 characters."
  };
  return messages[code] || "We could not complete that request. Please try again.";
}

function isPasswordAuthUser(firebaseUser) {
  return Boolean(firebaseUser?.providerData?.some((provider) => provider?.providerId === "password"));
}

function getEmailActionSettings() {
  if (typeof window === "undefined") return undefined;
  return {
    url: `${window.location.origin}/verify-email?from=email`,
    handleCodeInApp: false
  };
}

function shouldRetryEmailActionWithoutContinueUrl(error) {
  return [
    "auth/invalid-continue-uri",
    "auth/missing-continue-uri",
    "auth/unauthorized-continue-uri"
  ].includes(String(error?.code || ""));
}

async function sendAccountVerification(firebaseUser) {
  try {
    await sendEmailVerification(firebaseUser, getEmailActionSettings());
  } catch (error) {
    if (!shouldRetryEmailActionWithoutContinueUrl(error)) throw error;
    await sendEmailVerification(firebaseUser);
  }
}

async function sendVerificationToNewEmail(firebaseUser, nextEmail) {
  try {
    await verifyBeforeUpdateEmail(firebaseUser, nextEmail, getEmailActionSettings());
  } catch (error) {
    if (!shouldRetryEmailActionWithoutContinueUrl(error)) throw error;
    await verifyBeforeUpdateEmail(firebaseUser, nextEmail);
  }
}

function setStoredVerificationReturnPath(uid, path) {
  if (typeof window === "undefined" || !uid) return;
  const normalized = String(path || "").trim();
  if (normalized) {
    window.localStorage.setItem(`inksolver:verification-return:${uid}`, normalized);
  }
}

function takeStoredVerificationReturnPath(uid) {
  if (typeof window === "undefined" || !uid) return "";
  const key = `inksolver:verification-return:${uid}`;
  const value = window.localStorage.getItem(key) || "";
  window.localStorage.removeItem(key);
  return value;
}

function setPendingPrivacyConsent(uid, source) {
  if (typeof window === "undefined" || !uid) return;
  window.localStorage.setItem(
    `inksolver:privacy-consent:${uid}`,
    JSON.stringify({ policyVersion: PRIVACY_POLICY_VERSION, source })
  );
}

function getPendingPrivacyConsent(uid) {
  if (typeof window === "undefined" || !uid) return null;
  const key = `inksolver:privacy-consent:${uid}`;
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "null");
    return value && typeof value === "object" ? value : null;
  } catch (err) {
    window.localStorage.removeItem(key);
    return null;
  }
}

function clearPendingPrivacyConsent(uid) {
  if (typeof window === "undefined" || !uid) return;
  window.localStorage.removeItem(`inksolver:privacy-consent:${uid}`);
}

function App() {
  const NOTIFICATION_POLL_INTERVAL_MS = 60000;
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [password, setPassword] = useState("");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [authNotice, setAuthNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [verificationChecking, setVerificationChecking] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationChanging, setVerificationChanging] = useState(false);
  const [verificationError, setVerificationError] = useState("");
  const [verificationNotice, setVerificationNotice] = useState("");
  const [pendingVerificationEmail, setPendingVerificationEmail] = useState("");
  const [emailActionProcessing, setEmailActionProcessing] = useState(false);
  const [syncError, setSyncError] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [tokenExpiryAt, setTokenExpiryAt] = useState(null);
  const [sessionWarningOpen, setSessionWarningOpen] = useState(false);
  const [sessionSecondsLeft, setSessionSecondsLeft] = useState(SESSION_WARNING_SECONDS);
  const [sessionRefreshPending, setSessionRefreshPending] = useState(false);
  const [sessionWarningError, setSessionWarningError] = useState("");
  const [routeOverlayOpen, setRouteOverlayOpen] = useState(false);
  const [routeOverlayMessage, setRouteOverlayMessage] = useState("Loading...");
  const [pageBusyRegistry, setPageBusyRegistry] = useState({});
  const [profileUsername, setProfileUsername] = useState("");
  const [profilePhotoUrl, setProfilePhotoUrl] = useState("");
  const [profileFullName, setProfileFullName] = useState("");
  const [userRole, setUserRole] = useState(() => {
    try {
      return localStorage.getItem("inksolver:user-role") || "student";
    } catch {
      return "student";
    }
  });
  const [hasActiveTutor, setHasActiveTutor] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [syncDone, setSyncDone] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState("");
  const [notificationItems, setNotificationItems] = useState([]);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  const sessionWarningTimeoutRef = useRef(null);
  const sessionCountdownIntervalRef = useRef(null);
  const routeOverlayStartedAtRef = useRef(0);
  const routeOverlaySafetyTimeoutRef = useRef(null);
  const routeOverlayReleaseTimeoutRef = useRef(null);
  const previousLocationSignatureRef = useRef("");
  const notificationHostRef = useRef(null);
  const accountMenuHostRef = useRef(null);
  const notificationRequestRef = useRef(0);
  const authStateRequestRef = useRef(0);
  const verificationRefreshRef = useRef(false);
  const emailActionHandledRef = useRef("");

  const apiBase = process.env.REACT_APP_API_BASE || getDefaultApiBase();
  const location = useLocation();
  const navigate = useNavigate();
  const locationSignature = useMemo(() => getLocationSignature(location), [location]);
  const isPrivacyView = location.pathname === "/privacy";
  const isEmailPreferencesView = location.pathname === "/email-preferences";
  const isDeviceCheckView = location.pathname === "/device-check";
  const isCollaborationView = location.pathname.startsWith("/collaborate");
  const authReturnPath = location.pathname.startsWith("/collaborate/") ? getLocationSignature(location) : "";
  const isAdminAuthView = !user && location.pathname.startsWith("/admin");
  const isPublicAuthPath = location.pathname === "/login"
    || location.pathname === "/"
    || location.pathname === "/privacy"
    || location.pathname === "/email-preferences"
    || location.pathname === "/device-check"
    || location.pathname === "/verify-email"
    || location.pathname.startsWith("/admin")
    || location.pathname.startsWith("/collaborate/");

  const openPrivacyPolicy = useCallback(() => {
    if (location.pathname === "/privacy") return;
    const defaultHome = user ? (userRole === "tutor" ? "/tutor/dashboard" : "/dashboard") : "/login";
    navigate("/privacy", {
      state: { privacyReturnTo: getLocationSignature(location) || defaultHome }
    });
  }, [location, navigate, user, userRole]);

  const closePrivacyPolicy = useCallback(() => {
    const requestedPath = String(location.state?.privacyReturnTo || "").trim();
    const fallbackPath = user ? (userRole === "tutor" ? "/tutor/dashboard" : "/dashboard") : "/login";
    const returnPath = requestedPath.startsWith("/")
      && !requestedPath.startsWith("//")
      && requestedPath !== "/privacy"
      ? requestedPath
      : fallbackPath;
    navigate(returnPath, { replace: true });
  }, [location.state, navigate, user, userRole]);

  const activePageBusyEntry = useMemo(() => {
    const entries = Object.values(pageBusyRegistry || {});
    if (!entries.length) return null;
    return entries.sort((left, right) => Number(right?.at || 0) - Number(left?.at || 0))[0] || null;
  }, [pageBusyRegistry]);

  const shellBusy = routeOverlayOpen || Boolean(activePageBusyEntry);
  const shellBusyMessage = activePageBusyEntry?.message || routeOverlayMessage || "Loading...";

  const fetchEmailVerificationStatus = useCallback(async (firebaseUser, idToken) => {
    if (!firebaseUser || !isPasswordAuthUser(firebaseUser)) {
      return { required: false, verified: Boolean(firebaseUser?.emailVerified) };
    }

    const response = await fetch(`${apiBase}/api/auth/email-verification-status`, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || "Could not check email verification status.");
    }
    return payload;
  }, [apiBase]);

  const refreshEmailVerification = useCallback(async ({ navigateOnSuccess = true, quiet = false } = {}) => {
    const currentUser = auth.currentUser;
    if (!currentUser || verificationRefreshRef.current) return false;

    verificationRefreshRef.current = true;
    if (!quiet) {
      setVerificationChecking(true);
      setVerificationError("");
    }
    try {
      let refreshedToken = "";
      let tokenResult = null;
      let verification = { required: true };

      for (let attempt = 0; attempt < 4; attempt += 1) {
        await reloadFirebaseUser(currentUser);
        if (!currentUser.emailVerified) return false;

        tokenResult = await currentUser.getIdTokenResult(true);
        refreshedToken = tokenResult.token;
        verification = await fetchEmailVerificationStatus(currentUser, refreshedToken);
        if (!verification.required) break;

        if (attempt < 3) {
          await new Promise((resolve) => setTimeout(resolve, 450 * (attempt + 1)));
        }
      }

      if (verification.required || !refreshedToken || !tokenResult) {
        if (!quiet) {
          setVerificationNotice("Verification succeeded. We are securely finalizing your session and will continue automatically.");
        }
        return false;
      }

      setUser(currentUser);
      setEmailVerificationRequired(false);
      setPendingVerificationEmail("");
      setVerificationNotice("");
      setToken(refreshedToken);
      setTokenExpiryAt(new Date(tokenResult.expirationTime).getTime());
      setSyncError("");
      setSyncDone(false);

      if (navigateOnSuccess) {
        const returnTo = takeStoredVerificationReturnPath(currentUser.uid);
        navigate("/profile", {
          replace: true,
          state: returnTo ? { returnTo } : undefined
        });
      }
      return true;
    } catch (err) {
      if (!quiet) setVerificationError(friendlyAuthError(err));
      return false;
    } finally {
      verificationRefreshRef.current = false;
      if (!quiet) setVerificationChecking(false);
    }
  }, [fetchEmailVerificationStatus, navigate]);

  const reportPageBusy = useCallback((pageKey, busy, message = "Loading...") => {
    const normalizedKey = String(pageKey || "").trim() || "page";
    const normalizedMessage = String(message || "").trim() || "Loading...";
    setPageBusyRegistry((prev) => {
      const next = { ...(prev || {}) };
      if (busy) {
        next[normalizedKey] = {
          message: normalizedMessage,
          at: Date.now()
        };
      } else {
        delete next[normalizedKey];
      }
      return next;
    });
  }, []);

  const fetchNotifications = useCallback(async (options = {}) => {
    if (!token) {
      setNotificationItems([]);
      setNotificationUnreadCount(0);
      setNotificationsLoading(false);
      setNotificationsError("");
      return;
    }

    const requestId = notificationRequestRef.current + 1;
    notificationRequestRef.current = requestId;

    if (!options.quiet) {
      setNotificationsLoading(true);
    }
    if (!options.preserveError) {
      setNotificationsError("");
    }

    try {
      const response = await fetch(`${apiBase}/api/notifications?limit=12`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Failed to load notifications.");
      }

      const payload = await response.json().catch(() => ({}));
      if (notificationRequestRef.current !== requestId) return;
      setNotificationItems(Array.isArray(payload?.notifications) ? payload.notifications : []);
      setNotificationUnreadCount(Number(payload?.unreadCount || 0));
      setNotificationsError("");
    } catch (err) {
      if (notificationRequestRef.current !== requestId) return;
      setNotificationsError(err.message || "Failed to load notifications.");
    } finally {
      if (notificationRequestRef.current === requestId && !options.quiet) {
        setNotificationsLoading(false);
      }
    }
  }, [apiBase, token]);

  const markNotificationRead = useCallback(async (notificationId) => {
    if (!token || !notificationId) return;
    try {
      const response = await fetch(`${apiBase}/api/notifications/${notificationId}/read`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) return;
      const payload = await response.json().catch(() => ({}));
      setNotificationItems((prev) =>
        prev.map((item) => (
          String(item?.id || "") === String(notificationId)
            ? {
                ...item,
                readAt: payload?.notification?.readAt || item.readAt || new Date().toISOString()
              }
            : item
        ))
      );
      setNotificationUnreadCount(Number(payload?.unreadCount || 0));
    } catch (err) {
      // Ignore transient read failures and keep navigation responsive.
    }
  }, [apiBase, token]);

  const markAllNotificationsRead = useCallback(async () => {
    if (!token || !notificationUnreadCount) return;
    try {
      const response = await fetch(`${apiBase}/api/notifications/read-all`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!response.ok) return;
      setNotificationItems((prev) => prev.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })));
      setNotificationUnreadCount(0);
    } catch (err) {
      // Ignore transient failures; polling will recover state.
    }
  }, [apiBase, notificationUnreadCount, token]);

  const handleNotificationItemClick = useCallback((notification) => {
    if (!notification) return;
    if (!notification.readAt && notification.id) {
      void markNotificationRead(notification.id);
    }
    setNotificationsOpen(false);
    setAccountMenuOpen(false);
    let nextPath = String(notification.linkPath || "").trim();
    // Tutor notifications must always navigate student to My Tutor (/my-tutor), not /my-questions
    const isTutorNotification =
      notification.type === "tutor_feedback" ||
      notification.type === "assignment" ||
      notification.type === "assignment_graded" ||
      notification.type === "grades_published" ||
      notification.type === "tutor_invite" ||
      notification.type === "session" ||
      notification.entityType === "question_set";

    if (isTutorNotification && (nextPath === "/my-questions" || !nextPath)) {
      nextPath = "/my-tutor";
    }

    if (nextPath) {
      if (typeof window !== "undefined" && nextPath.startsWith("/collaborate/")) {
        const link = document.createElement("a");
        link.href = `${window.location.origin}${nextPath}`;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.click();
        return;
      }
      navigate(nextPath);
    }
  }, [markNotificationRead, navigate]);

  const toggleNotifications = useCallback(() => {
    setAccountMenuOpen(false);
    setNotificationsOpen((prev) => {
      const next = !prev;
      if (next) {
        void fetchNotifications({ quiet: false, preserveError: true });
      }
      return next;
    });
  }, [fetchNotifications]);

  const toggleAccountMenu = useCallback(() => {
    setNotificationsOpen(false);
    setAccountMenuOpen((prev) => !prev);
  }, []);

  const handleShellNavigationCapture = useCallback(
    (event) => {
      if (event.defaultPrevented) return;
      if (typeof event.button === "number" && event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const target = event.target;
      if (!target || typeof target.closest !== "function") return;
      const anchor = target.closest("a[href]");
      if (!anchor) return;
      if (anchor.getAttribute("target") === "_blank" || anchor.hasAttribute("download")) return;

      let url = null;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch (err) {
        return;
      }
      if (!url || url.origin !== window.location.origin) return;

      const nextSignature = `${url.pathname}${url.search}${url.hash}`;
      if (!nextSignature || nextSignature === locationSignature) return;

      setRouteOverlayMessage(getRouteLoadingMessage(url.pathname));
      setRouteOverlayOpen(true);
      routeOverlayStartedAtRef.current = Date.now();

      if (routeOverlayReleaseTimeoutRef.current) {
        clearTimeout(routeOverlayReleaseTimeoutRef.current);
        routeOverlayReleaseTimeoutRef.current = null;
      }
      if (routeOverlaySafetyTimeoutRef.current) {
        clearTimeout(routeOverlaySafetyTimeoutRef.current);
      }
      routeOverlaySafetyTimeoutRef.current = setTimeout(() => {
        setRouteOverlayOpen(false);
        routeOverlaySafetyTimeoutRef.current = null;
      }, MAX_ROUTE_OVERLAY_MS);
    },
    [locationSignature]
  );

  useEffect(() => {
    if (isAdminAuthView && mode !== "login") {
      setMode("login");
    }
  }, [isAdminAuthView, mode]);

  useEffect(() => {
    if (!authChecked || user || isPublicAuthPath) {
      return;
    }
    navigate("/login", { replace: true });
  }, [authChecked, isPublicAuthPath, navigate, user]);

  useEffect(() => {
    if (!previousLocationSignatureRef.current) {
      previousLocationSignatureRef.current = locationSignature;
      return undefined;
    }

    const previousSignature = previousLocationSignatureRef.current;
    previousLocationSignatureRef.current = locationSignature;

    if (!routeOverlayOpen || previousSignature === locationSignature) {
      return undefined;
    }

    const elapsed = Date.now() - routeOverlayStartedAtRef.current;
    const remaining = Math.max(0, MIN_ROUTE_OVERLAY_MS - elapsed);
    if (routeOverlayReleaseTimeoutRef.current) {
      clearTimeout(routeOverlayReleaseTimeoutRef.current);
    }
    routeOverlayReleaseTimeoutRef.current = setTimeout(() => {
      setRouteOverlayOpen(false);
      if (routeOverlaySafetyTimeoutRef.current) {
        clearTimeout(routeOverlaySafetyTimeoutRef.current);
        routeOverlaySafetyTimeoutRef.current = null;
      }
      routeOverlayReleaseTimeoutRef.current = null;
    }, remaining);

    return () => {
      if (routeOverlayReleaseTimeoutRef.current) {
        clearTimeout(routeOverlayReleaseTimeoutRef.current);
        routeOverlayReleaseTimeoutRef.current = null;
      }
    };
  }, [locationSignature, routeOverlayOpen]);

  useEffect(() => {
    return () => {
      if (routeOverlaySafetyTimeoutRef.current) {
        clearTimeout(routeOverlaySafetyTimeoutRef.current);
        routeOverlaySafetyTimeoutRef.current = null;
      }
      if (routeOverlayReleaseTimeoutRef.current) {
        clearTimeout(routeOverlayReleaseTimeoutRef.current);
        routeOverlayReleaseTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setNotificationItems([]);
      setNotificationUnreadCount(0);
      setNotificationsOpen(false);
      setNotificationsLoading(false);
      setNotificationsError("");
      return undefined;
    }

    const canPollNotifications = !isCollaborationView && (typeof document === "undefined" || document.visibilityState === "visible");

    if (canPollNotifications) {
      void fetchNotifications();
    }

    let pollTimer = null;
    const startPolling = () => {
      if (pollTimer || isCollaborationView || (typeof document !== "undefined" && document.visibilityState !== "visible")) return;
      pollTimer = window.setInterval(() => {
        void fetchNotifications({ quiet: true, preserveError: true });
      }, NOTIFICATION_POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (!pollTimer) return;
      window.clearInterval(pollTimer);
      pollTimer = null;
    };
    const handleRefresh = () => {
      void fetchNotifications({ quiet: true, preserveError: true });
    };
    const handleVisibilityChange = () => {
      if (typeof document !== "undefined" && document.visibilityState === "visible" && !isCollaborationView) {
        void fetchNotifications({ quiet: true, preserveError: true });
        startPolling();
        return;
      }
      stopPolling();
    };
    const handleWindowFocus = () => {
      if (isCollaborationView) return;
      void fetchNotifications({ quiet: true, preserveError: true });
      startPolling();
    };

    startPolling();
    window.addEventListener("inksolver:notifications-refresh", handleRefresh);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      stopPolling();
      window.removeEventListener("inksolver:notifications-refresh", handleRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [fetchNotifications, isCollaborationView, token]);

  useEffect(() => {
    if (!notificationsOpen && !accountMenuOpen) return undefined;
    const handleOutsidePointerDown = (event) => {
      const notificationHost = notificationHostRef.current;
      const accountHost = accountMenuHostRef.current;
      if ((notificationHost && notificationHost.contains(event.target)) || (accountHost && accountHost.contains(event.target))) {
        return;
      }
      setNotificationsOpen(false);
      setAccountMenuOpen(false);
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [accountMenuOpen, notificationsOpen]);

  useEffect(() => {
    setNotificationsOpen(false);
    setAccountMenuOpen(false);
  }, [locationSignature]);

  useEffect(() => {
    const unsub = onIdTokenChanged(auth, async (nextUser) => {
      const requestId = authStateRequestRef.current + 1;
      authStateRequestRef.current = requestId;
      setUser(nextUser);

      if (!nextUser) {
        setToken("");
        setEmailVerificationRequired(false);
        setPendingVerificationEmail("");
        setTokenExpiryAt(null);
        setSessionWarningOpen(false);
        setSessionSecondsLeft(SESSION_WARNING_SECONDS);
        setSessionWarningError("");
        setAuthChecked(true);
        return;
      }

      try {
        const [idToken, tokenResult] = await Promise.all([
          nextUser.getIdToken(),
          nextUser.getIdTokenResult()
        ]);

        const verification = await fetchEmailVerificationStatus(nextUser, idToken);
        if (authStateRequestRef.current !== requestId) return;

        if (verification.required) {
          setToken("");
          setTokenExpiryAt(null);
          setEmailVerificationRequired(true);
          setVerificationError("");
          setSessionWarningOpen(false);
          setAuthChecked(true);
          return;
        }

        setEmailVerificationRequired(false);
        setToken(idToken);
        setTokenExpiryAt(new Date(tokenResult.expirationTime).getTime());
        setSessionWarningError("");
      } catch (err) {
        if (authStateRequestRef.current !== requestId) return;
        setToken("");
        setTokenExpiryAt(null);
        setSessionWarningOpen(false);
        setSessionSecondsLeft(SESSION_WARNING_SECONDS);
        if (isPasswordAuthUser(nextUser) && !nextUser.emailVerified) {
          setEmailVerificationRequired(true);
          setVerificationError(err.message || "Could not check email verification status.");
        } else {
          setSessionWarningError(err.message || "Session refresh failed.");
        }
      } finally {
        if (authStateRequestRef.current === requestId) {
          setAuthChecked(true);
        }
      }
    });

    return () => unsub();
  }, [fetchEmailVerificationStatus]);

  useEffect(() => {
    if (!authChecked || !user || !emailVerificationRequired || emailActionProcessing) return;
    if (location.pathname !== "/verify-email") {
      navigate("/verify-email", { replace: true });
    }
  }, [authChecked, emailActionProcessing, emailVerificationRequired, location.pathname, navigate, user]);

  useEffect(() => {
    if (!user || !emailVerificationRequired) return undefined;

    const refreshQuietly = () => {
      if (document.visibilityState === "visible") {
        void refreshEmailVerification({ quiet: true });
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshQuietly();
    };

    const interval = setInterval(refreshQuietly, 5000);
    window.addEventListener("focus", refreshQuietly);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", refreshQuietly);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [emailVerificationRequired, refreshEmailVerification, user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const modeParam = params.get("mode");
    const actionCode = params.get("oobCode");
    const returnedFromEmail = location.pathname === "/verify-email" && params.get("from") === "email";

    if (!actionCode || modeParam !== "verifyEmail") {
      if (!returnedFromEmail || !authChecked) return undefined;
      if (auth.currentUser) {
        void refreshEmailVerification();
      } else {
        setAuthNotice("Email verified. Log in to continue.");
        navigate("/login", { replace: true });
      }
      return undefined;
    }

    const actionKey = `${modeParam}:${actionCode}`;
    if (emailActionHandledRef.current === actionKey) return undefined;
    emailActionHandledRef.current = actionKey;
    let cancelled = false;

    async function completeEmailAction() {
      setEmailActionProcessing(true);
      setVerificationError("");
      try {
        await applyActionCode(auth, actionCode);
        if (cancelled) return;
        if (auth.currentUser) {
          const completed = await refreshEmailVerification();
          if (!completed && !cancelled) {
            setVerificationError("The email was verified, but the account refresh is still pending. This page will keep checking automatically.");
          }
        } else {
          setAuthNotice("Email verified. Log in to continue.");
          navigate("/login", { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        const message = friendlyAuthError(err);
        if (auth.currentUser) {
          setVerificationError(message);
          navigate("/verify-email", { replace: true });
        } else {
          setError(message);
          navigate("/login", { replace: true });
        }
      } finally {
        setEmailActionProcessing(false);
      }
    }

    void completeEmailAction();
    return () => {
      cancelled = true;
    };
  }, [authChecked, location.pathname, location.search, navigate, refreshEmailVerification]);

  useEffect(() => {
    if (sessionWarningTimeoutRef.current) {
      clearTimeout(sessionWarningTimeoutRef.current);
      sessionWarningTimeoutRef.current = null;
    }

    if (!user || !tokenExpiryAt) {
      setSessionWarningOpen(false);
      setSessionSecondsLeft(SESSION_WARNING_SECONDS);
      return undefined;
    }

    const openWarning = () => {
      const secondsLeft = Math.max(0, Math.ceil((tokenExpiryAt - Date.now()) / 1000));
      setSessionSecondsLeft(secondsLeft);
      setSessionWarningOpen(true);
      setSessionWarningError("");
    };

    const warningDelay = tokenExpiryAt - Date.now() - SESSION_WARNING_MS;
    if (warningDelay <= 0) {
      openWarning();
    } else {
      sessionWarningTimeoutRef.current = setTimeout(openWarning, warningDelay);
    }

    return () => {
      if (sessionWarningTimeoutRef.current) {
        clearTimeout(sessionWarningTimeoutRef.current);
        sessionWarningTimeoutRef.current = null;
      }
    };
  }, [tokenExpiryAt, user]);

  useEffect(() => {
    if (sessionCountdownIntervalRef.current) {
      clearInterval(sessionCountdownIntervalRef.current);
      sessionCountdownIntervalRef.current = null;
    }

    if (!sessionWarningOpen || !tokenExpiryAt) {
      return undefined;
    }

    const tick = () => {
      const secondsLeft = Math.max(0, Math.ceil((tokenExpiryAt - Date.now()) / 1000));
      setSessionSecondsLeft(secondsLeft);
      if (secondsLeft <= 0) {
        if (sessionCountdownIntervalRef.current) {
          clearInterval(sessionCountdownIntervalRef.current);
          sessionCountdownIntervalRef.current = null;
        }
        void handleSessionLogout();
      }
    };

    tick();
    sessionCountdownIntervalRef.current = setInterval(tick, 1000);

    return () => {
      if (sessionCountdownIntervalRef.current) {
        clearInterval(sessionCountdownIntervalRef.current);
        sessionCountdownIntervalRef.current = null;
      }
    };
  }, [sessionWarningOpen, tokenExpiryAt]);

  useEffect(() => {
    let isMounted = true;

    async function syncUser() {
      if (!token) {
        if (isMounted) {
          setIsAdmin(false);
          setSyncDone(false);
        }
        return;
      }

      if (isMounted) {
        setSyncError("");
        // Avoid setting syncDone to false if it was already synced; prevents UI flashing on token extension
        setSyncDone((prev) => (prev ? true : false));
      }

      try {
        const response = await fetch(`${apiBase}/api/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Backend sync failed");
        }

        const data = await response.json();

        const consentUid = auth.currentUser?.uid;
        const pendingConsent = getPendingPrivacyConsent(consentUid);
        if (pendingConsent) {
          const consentResponse = await fetch(`${apiBase}/api/me/privacy-consent`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            },
            body: JSON.stringify(pendingConsent)
          });
          if (consentResponse.ok) {
            clearPendingPrivacyConsent(consentUid);
          } else {
            console.warn("Privacy consent will be recorded on the next account sync.");
          }
        }

        if (isMounted) {
          setSyncError("");
          setProfileUsername(data.username || "");
          setProfilePhotoUrl(data.picture || "");
          setProfileFullName(data.name || "");
          const resolvedRole = data.role || "student";
          setUserRole(resolvedRole);
          try {
            localStorage.setItem("inksolver:user-role", resolvedRole);
          } catch (storageErr) {}
          // Only redirect to onboarding if user has no username at all
          setNeedsOnboarding(!data.username);
          setSyncDone(true);
        }
      } catch (err) {
        if (isMounted) {
          setSyncError(err.message || "Failed to connect to the server.");
          setSyncDone((prev) => (prev ? true : false));
        }
      }
    }

    syncUser();

    return () => {
      isMounted = false;
    };
  }, [apiBase, token]);

  useEffect(() => {
    let isMounted = true;
    async function checkAdmin() {
      if (!token) return;
      try {
        const response = await fetch(`${apiBase}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          if (isMounted) setIsAdmin(false);
          return;
        }
        const payload = await response.json();
        if (isMounted) setIsAdmin(!!payload.isAdmin);
      } catch (err) {
        if (isMounted) setIsAdmin(false);
      }
    }
    checkAdmin();
    return () => {
      isMounted = false;
    };
  }, [apiBase, token]);

  useEffect(() => {
    let isMounted = true;
    async function checkActiveTutor() {
      if (!token || userRole === "tutor") {
        if (isMounted) setHasActiveTutor(false);
        return;
      }
      try {
        const response = await fetch(`${apiBase}/api/student/my-tutors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) {
          if (isMounted) setHasActiveTutor(false);
          return;
        }
        const payload = await response.json();
        if (isMounted) {
          setHasActiveTutor(Array.isArray(payload?.tutors) && payload.tutors.length > 0);
        }
      } catch (err) {
        if (isMounted) setHasActiveTutor(false);
      }
    }
    checkActiveTutor();
    return () => {
      isMounted = false;
    };
  }, [apiBase, token, userRole]);

  async function assertAdminAccess(idToken) {
    const response = await fetch(`${apiBase}/api/admin/me`, {
      headers: { Authorization: `Bearer ${idToken}` }
    });
    if (!response.ok) {
      throw new Error("Admin access denied for this account.");
    }
    const payload = await response.json().catch(() => ({}));
    if (!payload?.isAdmin) {
      throw new Error("Admin access denied for this account.");
    }
  }

  async function handleEmailAuth(event) {
    event.preventDefault();
    setError("");
    setAuthNotice("");
    setVerificationError("");
    setVerificationNotice("");

    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (mode === "signup" && !isAdminAuthView) {
      if (!privacyConsent) {
        setError("Review and accept the Privacy Policy before creating your account.");
        return;
      }
      const normalizedConfirmation = String(confirmEmail || "").trim().toLowerCase();
      if (normalizedEmail !== normalizedConfirmation) {
        setError("The email addresses do not match. Check them and try again.");
        return;
      }
    }

    setLoading(true);

    try {
      if (isAdminAuthView || mode === "login") {
        const credentials = await signInWithEmailAndPassword(auth, normalizedEmail, password);
        const idToken = await credentials.user.getIdToken();
        const verification = await fetchEmailVerificationStatus(credentials.user, idToken);
        if (verification.required) {
          setToken("");
          setEmailVerificationRequired(true);
          setStoredVerificationReturnPath(credentials.user.uid, authReturnPath);
          navigate("/verify-email", { replace: true });
          return;
        }

        if (isAdminAuthView) {
          await assertAdminAccess(idToken);
          navigate("/admin", { replace: true });
        } else {
          const cachedRole = (() => {
            try { return localStorage.getItem("inksolver:user-role"); } catch { return null; }
          })() || userRole;
          const targetHome = cachedRole === "tutor" ? "/tutor/dashboard" : "/dashboard";
          navigate(authReturnPath || targetHome, { replace: true });
        }
      } else {
        const credentials = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
        setPendingPrivacyConsent(credentials.user.uid, "email_signup");
        setProfileUsername("");
        setProfilePhotoUrl(credentials.user?.photoURL || "");
        setProfileFullName(credentials.user?.displayName || "");
        setNeedsOnboarding(true);
        setSyncDone(false);
        setToken("");
        setEmailVerificationRequired(true);
        setPendingVerificationEmail("");
        setStoredVerificationReturnPath(credentials.user.uid, authReturnPath);
        navigate("/verify-email", { replace: true });

        try {
          await sendAccountVerification(credentials.user);
          setVerificationNotice("Verification email sent. This page will update automatically after you open it.");
        } catch (verificationSendError) {
          setVerificationError(friendlyAuthError(verificationSendError));
        }
      }
    } catch (err) {
      if (isAdminAuthView) {
        await signOut(auth).catch(() => { });
      }
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleLogin() {
    setError("");
    setAuthNotice("");

    setLoading(true);

    try {
      const credentials = await signInWithPopup(auth, googleProvider);
      const isNewGoogleAccount = Boolean(getAdditionalUserInfo(credentials)?.isNewUser);
      if (!isAdminAuthView && isNewGoogleAccount) {
        setPendingPrivacyConsent(credentials.user.uid, "google_signup");
      }
      if (isAdminAuthView) {
        const idToken = await credentials.user.getIdToken();
        await assertAdminAccess(idToken);
        navigate("/admin", { replace: true });
      } else {
        const cachedRole = (() => {
          try { return localStorage.getItem("inksolver:user-role"); } catch { return null; }
        })() || userRole;
        const targetHome = cachedRole === "tutor" ? "/tutor/dashboard" : "/dashboard";
        navigate(authReturnPath || targetHome, { replace: true });
      }
    } catch (err) {
      if (isAdminAuthView) {
        await signOut(auth).catch(() => { });
      }
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordReset() {
    const normalizedEmail = String(email || "").trim();
    setError("");
    setAuthNotice("");
    if (!normalizedEmail) {
      setError("Enter your email address first, then choose Forgot password.");
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, normalizedEmail);
      setAuthNotice("If an account exists for this email, a reset link is on its way.");
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setResetLoading(false);
    }
  }

  async function handleResendVerification() {
    const currentUser = auth.currentUser;
    if (!currentUser) return false;

    setVerificationSending(true);
    setVerificationError("");
    setVerificationNotice("");
    try {
      if (pendingVerificationEmail) {
        await sendVerificationToNewEmail(currentUser, pendingVerificationEmail);
      } else {
        await sendAccountVerification(currentUser);
      }
      setVerificationNotice(`Verification email sent to ${pendingVerificationEmail || currentUser.email}.`);
      return true;
    } catch (err) {
      setVerificationError(friendlyAuthError(err));
      return false;
    } finally {
      setVerificationSending(false);
    }
  }

  async function handleChangeVerificationEmail(nextEmail) {
    const currentUser = auth.currentUser;
    const normalizedEmail = String(nextEmail || "").trim().toLowerCase();
    if (!currentUser || !normalizedEmail) return false;
    if (normalizedEmail === String(currentUser.email || "").trim().toLowerCase()) {
      setVerificationError("Enter a different email address.");
      return false;
    }

    setVerificationChanging(true);
    setVerificationError("");
    setVerificationNotice("");
    try {
      await sendVerificationToNewEmail(currentUser, normalizedEmail);
      setPendingVerificationEmail(normalizedEmail);
      setVerificationNotice(`Verification email sent to ${normalizedEmail}. Your account email will update automatically after verification.`);
      return true;
    } catch (err) {
      setVerificationError(friendlyAuthError(err));
      return false;
    } finally {
      setVerificationChanging(false);
    }
  }

  async function handleVerificationSignOut() {
    setVerificationError("");
    setVerificationNotice("");
    setPendingVerificationEmail("");
    setEmailVerificationRequired(false);
    setMode("login");
    setPassword("");
    setConfirmEmail("");
    try {
      localStorage.removeItem("inksolver:user-role");
    } catch (storageErr) {}
    setUserRole("student");
    await signOut(auth);
    navigate("/login", { replace: true });
  }

  function handleAuthModeChange(nextMode) {
    setMode(nextMode === "signup" ? "signup" : "login");
    setPassword("");
    setConfirmEmail("");
    setPrivacyConsent(false);
    setError("");
    setAuthNotice("");
  }

  async function handleExtendSession() {
    if (!auth.currentUser) {
      return;
    }

    setSessionRefreshPending(true);
    setSessionWarningError("");

    try {
      const refreshedToken = await auth.currentUser.getIdToken(true);
      const tokenResult = await auth.currentUser.getIdTokenResult();
      setToken(refreshedToken);
      setTokenExpiryAt(new Date(tokenResult.expirationTime).getTime());
      setSessionWarningOpen(false);
      setSessionSecondsLeft(SESSION_WARNING_SECONDS);
    } catch (err) {
      setSessionWarningError(err.message || "Could not extend the session.");
    } finally {
      setSessionRefreshPending(false);
    }
  }

  async function handleSessionLogout() {
    setSessionWarningOpen(false);
    setSessionSecondsLeft(SESSION_WARNING_SECONDS);
    setSessionWarningError("");
    try {
      localStorage.removeItem("inksolver:user-role");
    } catch (storageErr) {}
    setUserRole("student");
    await signOut(auth);
  }

  async function handleSignOut() {
    setNotificationsOpen(false);
    setAccountMenuOpen(false);
    await handleSessionLogout();
  }

  if (!authChecked || emailActionProcessing) {
    return (
      <div className="App">
        <div className="app-auth-loading">
          <div className="app-shell-busy-card">
            <div className="app-shell-busy-spinner" aria-hidden="true" />
            <div className="app-shell-busy-copy">
              <div className="app-shell-busy-title">{emailActionProcessing ? "Verifying your email..." : "Loading..."}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isEmailPreferencesView) {
    return (
      <div className="App">
        <EmailPreferencesPage
          apiBase={apiBase}
          authToken={token}
          signedIn={Boolean(user)}
        />
      </div>
    );
  }

  if (isDeviceCheckView) {
    return (
      <div className="App">
        <DeviceCheck apiBase={apiBase} token={token} user={user} />
      </div>
    );
  }

  if (user && emailVerificationRequired) {
    return (
      <div className="App">
        <EmailVerificationScreen
          email={user.email}
          pendingEmail={pendingVerificationEmail}
          sending={verificationSending}
          changing={verificationChanging}
          checking={verificationChecking}
          error={verificationError}
          notice={verificationNotice}
          onResend={handleResendVerification}
          onChangeEmail={handleChangeVerificationEmail}
          onSignOut={handleVerificationSignOut}
        />
        {isPrivacyView && <PrivacyPolicyOverlay onClose={closePrivacyPolicy} />}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="App">
        <AuthScreen
          mode={mode}
          email={email}
          confirmEmail={confirmEmail}
          password={password}
          loading={loading}
          resetLoading={resetLoading}
          error={error}
          notice={authNotice}
          allowSignup={!isAdminAuthView}
          privacyConsent={privacyConsent}
          onModeChange={handleAuthModeChange}
          onEmailChange={(value) => {
            setEmail(value);
            setError("");
            setAuthNotice("");
          }}
          onConfirmEmailChange={(value) => {
            setConfirmEmail(value);
            setError("");
          }}
          onPasswordChange={(value) => {
            setPassword(value);
            setError("");
          }}
          onPrivacyConsentChange={(checked) => {
            setPrivacyConsent(checked);
            setError("");
          }}
          onOpenPrivacy={openPrivacyPolicy}
          onSubmit={handleEmailAuth}
          onGoogleLogin={handleGoogleLogin}
          onForgotPassword={handlePasswordReset}
        />
        {isPrivacyView && <PrivacyPolicyOverlay onClose={closePrivacyPolicy} />}
      </div>
    );
  }

  const isTourFlow = location.pathname === "/solve-first-problem"
    || location.pathname === "/start-your-journey"
    || location.state?.fromTour
    || location.state?.fromCalibration;
  const isPracticeView = location.pathname.includes("/practice")
    || location.pathname.includes("/solve-first-problem")
    || location.pathname.includes("/custom-question");
  const syncFailedBeforeProfileResolved = Boolean(syncError) && !syncDone;
  const profileSyncPending = Boolean(user && token && !syncDone && !syncError);
  const shouldForceOnboarding = needsOnboarding || syncFailedBeforeProfileResolved;
  const showOnboardingTopBar = shouldForceOnboarding || isTourFlow;

  return (
    <div className={`App ${isCollaborationView ? "collaboration-app" : ""} ${isPracticeView ? "practice-app" : ""}`.trim()}>
      {syncError && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0,
          background: "#ef4444", color: "#ffffff",
          textAlign: "center", padding: "10px",
          zIndex: 9999, fontSize: "14px", fontWeight: "600",
          boxShadow: "0 4px 12px rgba(239, 68, 68, 0.2)"
        }}>
          ⚠️ Offline Mode: {syncError}. Your progress may not be saved.
        </div>
      )}
      <div
        className={`app-shell ${shellBusy ? "is-busy" : ""} ${showOnboardingTopBar || isCollaborationView ? "no-sidebar" : ""} ${isCollaborationView ? "collaboration-shell" : ""}`}
        aria-busy={shellBusy}
        onClickCapture={handleShellNavigationCapture}
      >
        {!showOnboardingTopBar && !isCollaborationView && (
          <aside className="sidebar">
            <div className="sidebar-brand">
              <img src={inksolverLogo} alt="InkSolver" className="brand-logo" />
            </div>
            <nav className="sidebar-nav">
              {userRole === "tutor" ? (
                <>
                  <NavLink to="/tutor/dashboard" className="nav-link" title="Dashboard">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="7" height="9" rx="1.5" />
                      <rect x="14" y="3" width="7" height="5" rx="1.5" />
                      <rect x="14" y="12" width="7" height="9" rx="1.5" />
                      <rect x="3" y="16" width="7" height="5" rx="1.5" />
                    </svg>
                    <span className="nav-link-text">Dashboard</span>
                  </NavLink>
                  <NavLink to="/tutor/students" className="nav-link" title="My Students">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                    <span className="nav-link-text">My Students</span>
                  </NavLink>
                  <NavLink to="/tutor/assignments" className="nav-link" title="Assignments">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                      <path d="M9 13l2 2 4-4" />
                    </svg>
                    <span className="nav-link-text">Assignments</span>
                  </NavLink>
                  <NavLink to="/tutor/performance" className="nav-link" title="Parent Reports & Performance">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="18" y1="20" x2="18" y2="10" />
                      <line x1="12" y1="20" x2="12" y2="4" />
                      <line x1="6" y1="20" x2="6" y2="14" />
                      <line x1="2" y1="20" x2="22" y2="20" />
                    </svg>
                    <span className="nav-link-text">Parent Reports</span>
                  </NavLink>
                  <NavLink to="/tutor/calendar" className="nav-link" title="Calendar">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span className="nav-link-text">Calendar</span>
                  </NavLink>
                  <NavLink to="/my-questions" className="nav-link" title="Question Bank">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                    <span className="nav-link-text">Question Bank</span>
                  </NavLink>
                </>
              ) : profileSyncPending ? (
                <div style={{ padding: "16px 14px", fontSize: "12px", color: "var(--muted)" }}>
                  Loading navigation...
                </div>
              ) : (
                <>
                  <NavLink to="/dashboard" className="nav-link" title="Dashboard">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="7" height="9" rx="1.5" />
                      <rect x="14" y="3" width="7" height="5" rx="1.5" />
                      <rect x="14" y="12" width="7" height="9" rx="1.5" />
                      <rect x="3" y="16" width="7" height="5" rx="1.5" />
                    </svg>
                    <span className="nav-link-text">Dashboard</span>
                  </NavLink>
                  <NavLink to="/chapters" className="nav-link" title="Chapters">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polygon points="12 2 2 7 12 12 22 7 12 2" />
                      <polyline points="2 17 12 22 22 17" />
                      <polyline points="2 12 12 17 22 12" />
                    </svg>
                    <span className="nav-link-text">Chapters</span>
                  </NavLink>
                  <NavLink to="/my-questions" className="nav-link" title="My Questions">
                    <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                    </svg>
                    <span className="nav-link-text">My Questions</span>
                  </NavLink>
                  {hasActiveTutor && (
                    <NavLink to="/my-tutor" className="nav-link" title="My Tutor">
                      <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                        <path d="M6 12v5c3 3 9 3 12 0v-5" />
                      </svg>
                      <span className="nav-link-text">My Tutor</span>
                    </NavLink>
                  )}
                </>
              )}
              {isAdmin && (
                <NavLink to="/admin/chapters" className="nav-link" title="Admin">
                  <svg className="nav-link-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  <span className="nav-link-text">Admin</span>
                </NavLink>
              )}
            </nav>
            <div className="sidebar-footer">
              <NotificationBell
                hostRef={notificationHostRef}
                open={notificationsOpen}
                unreadCount={notificationUnreadCount}
                notifications={notificationItems}
                loading={notificationsLoading}
                error={notificationsError}
                onToggle={toggleNotifications}
                onItemClick={handleNotificationItemClick}
                onMarkAllRead={markAllNotificationsRead}
              />
              <div ref={accountMenuHostRef} className={`sidebar-account-shell ${accountMenuOpen ? "open" : ""}`.trim()}>
                <button
                  type="button"
                  className="profile-menu-trigger"
                  aria-haspopup="menu"
                  aria-expanded={accountMenuOpen}
                  onClick={toggleAccountMenu}
                >
                  <div className="sidebar-avatar">
                    {profilePhotoUrl
                      ? <img src={profilePhotoUrl} alt="Profile" />
                      : (
                        profileFullName
                          ? profileFullName.trim().split(" ").filter(Boolean).map((n) => n[0]).join("").toUpperCase()
                          : profileUsername
                            ? profileUsername.slice(0, 2).toUpperCase()
                            : user?.email
                              ? user.email.slice(0, 2).toUpperCase()
                              : "??"
                      )
                    }
                  </div>
                  <span className="sidebar-account-label">{profileUsername || user.email}</span>
                  <span className={`sidebar-account-chevron ${accountMenuOpen ? "open" : ""}`.trim()} aria-hidden="true">
                    ▾
                  </span>
                </button>
                {accountMenuOpen && (
                  <div className="sidebar-account-dropdown" role="menu" aria-label="Account">
                    <Link
                      to="/profile"
                      className="sidebar-account-menu-item"
                      role="menuitem"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/email-preferences"
                      className="sidebar-account-menu-item"
                      role="menuitem"
                      onClick={() => setAccountMenuOpen(false)}
                    >
                      Email preferences
                    </Link>
                    <button
                      type="button"
                      className="sidebar-account-menu-item"
                      role="menuitem"
                      onClick={() => {
                        setAccountMenuOpen(false);
                        void handleSignOut();
                      }}
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </aside>
        )}

        {/* ONBOARDING TOP BAR (Shows only when sidebar is hidden) */}
        {showOnboardingTopBar && !isCollaborationView && (
          <OnboardingProgressHeader
            currentStep={location.pathname === "/profile"
              ? "profile"
              : location.pathname === "/start-your-journey"
                ? "journey"
                : "problem"}
          >
              {location.pathname === "/solve-first-problem" && (
                <div style={{ display: "flex", gap: "12px", marginLeft: "auto" }}>
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => navigate("/profile", { state: { fromTour: true } })}
                    style={{ whiteSpace: "nowrap", fontSize: "13px", marginLeft: "auto" }}
                  >
                    Edit Profile
                  </button>
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => window.dispatchEvent(new Event("start-demo-tour"))}
                    style={{
                      whiteSpace: "nowrap",
                      fontSize: "13px",
                      color: "#4f46e5",
                      borderColor: "#c7d2fe",
                      backgroundColor: "#ffffff"
                    }}
                  >
                    ? Guide
                  </button>
                </div>
              )}
              {location.pathname === "/start-your-journey" && (
                <button
                  className="secondary-button"
                  type="button"
                  onClick={() => navigate("/solve-first-problem", { state: { fromTour: true } })}
                  style={{ whiteSpace: "nowrap", fontSize: "13px", marginLeft: "auto" }}
                >
                  Back
                </button>
              )}
          </OnboardingProgressHeader>
        )}

        <main className={`main-content ${isCollaborationView ? "collaboration-main-content" : ""} ${isPracticeView ? "practice-main-content" : ""}`.trim()}>
          <Routes>
            <Route
              path="/dashboard"
              element={
                // Never show Dashboard until profile sync has resolved; failed sync should stay in onboarding/profile.
                shouldForceOnboarding
                  ? <Navigate to="/profile" replace />
                  : profileSyncPending
                    ? <div className="analysis-box"><div className="question-meta">Loading profile...</div></div>
                    : userRole === "tutor"
                      ? <Navigate to="/tutor/dashboard" replace />
                      : <Dashboard
                        user={user}
                        username={profileUsername}
                        apiBase={apiBase}
                        token={token}
                        onPageBusyChange={reportPageBusy}
                      />
              }
            />
            {/* Tutor Module Routes */}
            <Route
              path="/tutor/dashboard"
              element={<TutorDashboard apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/tutor/students"
              element={<TutorStudents apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/tutor/assignments"
              element={<TutorAssignments apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/tutor/assignments/:setId"
              element={<TutorAssignmentDetail apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/tutor/performance"
              element={<TutorPerformanceReports apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/tutor/calendar"
              element={<TutorCalendarPage apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/my-tutor"
              element={
                hasActiveTutor ? (
                  <MyTutor apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              }
            />
            <Route
              path="/chapters"
              element={
                userRole === "tutor" ? (
                  <Navigate to="/tutor/dashboard" replace />
                ) : (
                  <Chapters apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />
                )
              }
            />
            <Route
              path="/chapters/:chapterId/frames/:frameId"
              element={
                userRole === "tutor" ? (
                  <Navigate to="/tutor/dashboard" replace />
                ) : (
                  <FrameAttempts apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />
                )
              }
            />
            <Route
              path="/chapters/:chapterId"
              element={
                userRole === "tutor" ? (
                  <Navigate to="/tutor/dashboard" replace />
                ) : (
                  <ChapterHome apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />
                )
              }
            />
            <Route
              path="/my-questions"
              element={<MyQuestions apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/questions/create"
              element={<CreateQuestionPage apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/my-questions/create"
              element={<CreateQuestionPage apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/my-questions/:questionId/attempts"
              element={<CustomQuestionAttempts apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/practice/custom/:questionId"
              element={<Practice apiBase={apiBase} token={token} user={user} onPageBusyChange={reportPageBusy} isCustomQuestion={true} />}
            />
            <Route
              path="/my-questions/:questionId/solve"
              element={<Practice apiBase={apiBase} token={token} user={user} onPageBusyChange={reportPageBusy} isCustomQuestion={true} />}
            />
            <Route
              path="/practice/:chapterId"
              element={
                userRole === "tutor" ? (
                  <Navigate to="/tutor/dashboard" replace />
                ) : (
                  <Practice apiBase={apiBase} token={token} user={user} onPageBusyChange={reportPageBusy} />
                )
              }
            />
            <Route
              path="/collaborate/:roomToken"
              element={<CollaborationPractice apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/attempts/:attemptId"
              element={<AttemptReview apiBase={apiBase} token={token} user={user} userRole={userRole} onPageBusyChange={reportPageBusy} />}
            />
            {isAdmin && (
              <Route path="/admin" element={<Admin />}>
                <Route index element={<Navigate to="/admin/chapters" replace />} />
                <Route path="chapters" element={<AdminChapters apiBase={apiBase} token={token} />} />
                <Route path="chapters/new" element={<AdminChapterEditor apiBase={apiBase} token={token} />} />
                <Route path="chapters/:chapterId" element={<AdminChapterEditor apiBase={apiBase} token={token} />} />
                <Route path="frames" element={<AdminFrames apiBase={apiBase} token={token} />} />
                <Route path="frames/new" element={<AdminFrameEditor apiBase={apiBase} token={token} />} />
                <Route path="frames/:frameId" element={<AdminFrameEditor apiBase={apiBase} token={token} />} />
                <Route path="solution-graphs" element={<AdminSolutionGraphs apiBase={apiBase} token={token} />} />
                <Route path="solution-graphs/new" element={<AdminSolutionGraphEditor apiBase={apiBase} token={token} />} />
                <Route path="solution-graphs/:graphId" element={<AdminSolutionGraphEditor apiBase={apiBase} token={token} />} />
                <Route path="users" element={<Users apiBase={apiBase} token={token} />} />
              </Route>
            )}
            <Route
              path="/profile"
              element={
                <Profile
                  user={user}
                  apiBase={apiBase}
                  token={token}
                  isOnboarding={showOnboardingTopBar}
                  onProfileSaved={(uname, photoUrl, fullName, isFormSave, savedRole) => {
                    setProfileUsername(uname);
                    if (fullName) setProfileFullName(fullName);
                    if (photoUrl) setProfilePhotoUrl(photoUrl);
                    if (savedRole) {
                      setUserRole(savedRole);
                      try {
                        localStorage.setItem("inksolver:user-role", savedRole);
                      } catch (storageErr) {}
                    }
                    if (isFormSave && (needsOnboarding || location.state?.fromTour || location.state?.fromCalibration || location.state?.returnTo)) {
                      setNeedsOnboarding(false);
                      let nextPath = location.state?.returnTo;
                      if (!nextPath) {
                        if (savedRole === "tutor" || userRole === "tutor") {
                          nextPath = "/tutor/dashboard";
                        } else {
                          nextPath = (needsOnboarding || location.state?.fromCalibration)
                            ? "/device-check?onboarding=1"
                            : "/solve-first-problem";
                        }
                      }
                      navigate(nextPath, { replace: true });
                    } else if (isFormSave) {
                      setNeedsOnboarding(false);
                    }
                  }}
                />
              }
            />
            <Route
              path="/slate-lab"
              element={<SlateInputLab />}
            />
            <Route
              path="/solve-first-problem"
              element={<SolveFirstProblem apiBase={apiBase} token={token} onPageBusyChange={reportPageBusy} />}
            />
            <Route
              path="/start-your-journey"
              element={<StartYourJourney />}
            />
            <Route
              path="/privacy"
              element={<div className="privacy-route-placeholder" aria-hidden="true" />}
            />
            <Route
              path="/login"
              element={profileSyncPending ? <div className="analysis-box"><div className="question-meta">Loading profile...</div></div> : <Navigate to={shouldForceOnboarding ? "/profile" : (userRole === "tutor" ? "/tutor/dashboard" : "/dashboard")} replace />}
            />
            <Route
              path="/"
              element={profileSyncPending ? <div className="analysis-box"><div className="question-meta">Loading profile...</div></div> : <Navigate to={shouldForceOnboarding ? "/profile" : (userRole === "tutor" ? "/tutor/dashboard" : "/dashboard")} replace />}
            />
            <Route
              path="*"
              element={profileSyncPending ? <div className="analysis-box"><div className="question-meta">Loading profile...</div></div> : <Navigate to={shouldForceOnboarding ? "/profile" : (userRole === "tutor" ? "/tutor/dashboard" : "/dashboard")} replace />}
            />
          </Routes>
        </main>
        {shellBusy && (
          <div className="app-shell-busy-overlay" role="status" aria-live="polite" aria-label={shellBusyMessage}>
            <div className="app-shell-busy-card">
              <div className="app-shell-busy-spinner" aria-hidden="true" />
              <div className="app-shell-busy-copy">
                <div className="app-shell-busy-title">{shellBusyMessage}</div>
                <div className="app-shell-busy-line" aria-hidden="true">
                  <span />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {isPrivacyView && <PrivacyPolicyOverlay onClose={closePrivacyPolicy} />}
      {sessionWarningOpen && (
        <div className="feedback-modal" role="dialog" aria-modal="true" aria-labelledby="session-warning-title">
          <div className="feedback-card">
            <div className="feedback-card-header">
              <h2 id="session-warning-title">Session Expiring</h2>
              <div className="session-warning-countdown">{sessionSecondsLeft}s</div>
            </div>
            <p className="session-warning-body">
              Your session is about to expire. Click OK to refresh it and keep your work, or log out now.
            </p>
            {sessionWarningError && <div className="error-text">{sessionWarningError}</div>}
            <div className="feedback-actions">
              <button
                className="primary-button"
                type="button"
                onClick={handleExtendSession}
                disabled={sessionRefreshPending}
              >
                {sessionRefreshPending ? "Refreshing..." : "OK"}
              </button>
              <button className="secondary-button" type="button" onClick={handleSessionLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
